import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  FormHelperText,
  Grid,
  TextField,
  Typography,
} from "@mui/material";
import { AlertContext } from "contexts/Alert";
import { SlideAnimationContext } from "contexts/SlideAnimation";
import { useContext, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  OrganizationVaProfile,
  VaNicheSpecificDetails,
  VaNicheType,
  VaOnboardingType,
  VaOrganizationProfileStatus,
} from "schemas/dashboard";
import { CustomEvent } from "schemas/functions";

import VideoEmbed from "components/VideoEmbed";
import { vaOnboardingNavigate } from "features/Auth/Onboarding/helper";
import { getSource } from "utils/tracking";
import { makeDeepCopy } from "utils/updateLocalState";

import styles from "./styles";

interface Props {
  organizationVaProfile: OrganizationVaProfile | null;
  saveVaProfileForm: (
    organizationVaProfile: OrganizationVaProfile | null,
    page?: string,
    source?: string | null,
    skipMovingNext?: boolean,
  ) => void;
}

const VaNichePickForm = ({
  organizationVaProfile,
  saveVaProfileForm,
}: Props) => {
  const { setAlert } = useContext(AlertContext);
  const location = useLocation();
  const navigate = useNavigate();
  const slideContext = useContext(SlideAnimationContext);

  const hasCompleted =
    organizationVaProfile?.status === VaOrganizationProfileStatus.completed;

  const [niches, setNiches] = useState<VaNicheSpecificDetails[]>([
    { name: "", type: VaNicheType.general, is_top_performing: false },
    { name: "", type: VaNicheType.general, is_top_performing: false },
    { name: "", type: VaNicheType.general, is_top_performing: false },
    { name: "", type: VaNicheType.specific, is_top_performing: false },
  ]);

  const allQuestionsAnswered = () => {
    return niches.every((niche: VaNicheSpecificDetails) => {
      return niche.name?.trim()?.length > 0;
    });
  };

  const handleBack = async () => {
    if (slideContext) {
      await slideContext.slideOutDown();
      slideContext.slideInDown();
    }
    navigate(
      `/${vaOnboardingNavigate(location.pathname, "back")}${location.search}`,
    );
  };

  const handleNext = async (e: CustomEvent) => {
    e.preventDefault();
    if (!allQuestionsAnswered()) {
      setAlert("Please fill out all niches", "error");
      return;
    }
    const copy = makeDeepCopy(organizationVaProfile);
    const source = getSource();
    copy.niches = niches;
    saveVaProfileForm(copy, location.pathname, source);
  };

  const onChange = (e: CustomEvent, index: number) => {
    const { name, value } = e.target;
    setNiches((prev) => {
      const copy = makeDeepCopy(prev);
      copy[index][name] = value;
      return copy;
    });
  };

  const handleTopPerforming = (e: CustomEvent, index: number) => {
    const { checked } = e.target;
    setNiches((prev) => {
      const copy = makeDeepCopy(prev);
      const hasCheckedIndex = copy.findIndex(
        (x: VaNicheSpecificDetails) => x.is_top_performing,
      );
      if (hasCheckedIndex !== index && hasCheckedIndex > -1 && checked) {
        copy[hasCheckedIndex]["is_top_performing"] = false;
      }
      copy[index]["is_top_performing"] = checked;
      return copy;
    });
  };

  const renderNicheQuestion = (index: number, type: VaNicheType) => {
    return (
      <Grid container alignItems="center" sx={{ mb: 2 }} gap={2}>
        <Grid item xs={12} md={7}>
          <TextField
            sx={styles.textField}
            fullWidth
            name="name"
            placeholder={"Type niche here..."}
            onChange={(e) => onChange(e, index)}
            required
            value={niches[index].name}
          />
        </Grid>

        <Grid item xs={12} md={3}>
          <FormControlLabel
            onChange={(e) => handleTopPerforming(e, index)}
            control={
              <Checkbox
                icon={<Box component="i" className="fa-regular fa-square" />}
                checkedIcon={
                  <Box component="i" className="fa-solid fa-check-square" />
                }
                checked={niches[index].is_top_performing}
              />
            }
            label={<FormHelperText>My top performing niche</FormHelperText>}
          />
        </Grid>
      </Grid>
    );
  };

  useEffect(() => {
    if (
      organizationVaProfile?.niches &&
      Object.keys(organizationVaProfile?.niches)?.length > 0
    ) {
      setNiches(Object.values(organizationVaProfile?.niches));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organizationVaProfile?.niches]);

  return (
    <>
      <Typography variant="h3" gutterBottom sx={{ textAlign: "center", mt: 0 }}>
        {organizationVaProfile?.onboardingType === VaOnboardingType.book_call
          ? "Prework: Picking your niches"
          : "Let's get you onboarded: Picking your niches"}{" "}
        🚀
      </Typography>
      <Typography variant="subtitle1" gutterBottom sx={{ mb: 3, fontSize: 14 }}>
        The first step is to pick your niches. We recommend selecting{" "}
        <strong>3 general niches and 1 specific niche</strong>. Here are a few
        ideas to help you choose:
        <ul>
          <li>Niches where you’ve had the most success in the past</li>
          <li>
            Niches that have generated positive responses from your own pitching
          </li>
          <li>Niches where you have a unique edge when creating content</li>
        </ul>
        A few things to know:
        <ul>
          <li>
            Bento's copywriters will create 5 email templates for you: 1 general
            (niche-agnostic) template and 4 niche-specific templates. Be sure to
            select the 4 niches you'd like templates for.
          </li>
          <li>
            You can always create additional pitches for other niches throughout
            your subscription. To use those templates, just email your VA at{" "}
            <strong>
              <a href="mailto:va@onbento.com">va@onbento.com</a>
            </strong>{" "}
            and let them know which ones to use.
          </li>
        </ul>
      </Typography>

      <Grid
        container
        justifyContent="center"
        flexDirection="column"
        sx={{ my: 3 }}
      >
        <FormHelperText sx={{ textAlign: "center" }}>
          Watch this video to guide you on how to choose your niches!
        </FormHelperText>
        <VideoEmbed
          link={
            "https://storage.googleapis.com/hatchways-crm.appspot.com/app-assets/SELECTING_YOUR_NICHES.mp4"
          }
          eventName="VA Onboarding Flow - How To Choose Niche Video Clicked"
        />
      </Grid>
      <form onSubmit={handleNext}>
        {niches?.length > 0 &&
          niches?.map((niche, index) =>
            renderNicheQuestion(index, niche?.type || VaNicheType.general),
          )}

        <Grid item xs="auto" container columnGap={1} sx={{ mt: 4 }}>
          {!hasCompleted && (
            <Grid item xs="auto">
              <Button onClick={handleBack} color="secondary" disableElevation>
                Back
              </Button>
            </Grid>
          )}
          <Grid item xs></Grid>
          <Grid item xs="auto">
            <Button disableElevation variant="contained" type="submit">
              Next{" "}
              <Box
                sx={{ ml: 1 }}
                component="i"
                className="fa-solid fa-arrow-right"
              />
            </Button>
          </Grid>
        </Grid>
      </form>
    </>
  );
};

export default VaNichePickForm;
